/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import { get as _get, debounce } from 'lodash';
import { useDispatch } from 'react-redux';
import { Draggable } from 'react-beautiful-dnd';
import { useParams } from 'react-router-dom';
import { Constants } from '@pwc/pwc-deals-web-common';
import { postExcelLibraryItemSVGScaleFactor } from 'features/contentLibrary/libraryItemService';
import { isTableauLibraryItem } from 'common/components/SectionArea/libraryItemUtils.ts';
import { TableauPreview } from 'common/components/SelectReport/TableauPreview.tsx';
import LibraryItemCardWidgetOptions from 'common/components/LibraryItemCardWidgetOptions';
import LibraryItemCard from 'features/analyses/common/LibraryItemCard/LibraryItemCard';
import RichTextLibraryItemCard from 'features/analyses/common/LibraryItemCard/RichTextLibraryItemCard';
import PowerBILibraryItemCard from 'features/analyses/common/LibraryItemCard/PowerBILibraryItemCard';
import ExcelPPTLibraryItemCard from 'features/analyses/common/LibraryItemCard/ExcelPPTLibraryItemCard';
import { ExcelPPTLibraryItemCard as ExcelPPTLibraryItemCardNew } from 'features/analyses/common/LibraryItemCard/components/ExcelPPTLibraryItemCard';
import {
  setLibraryItemFinishPosition,
  setLibraryItemStartPosition,
} from 'common/redux/reportFormat/reportFormatSlice';
import featureFlags from 'common/helpers/featureFlags';

import { useSections } from '../FreeformAnalysisForm/FreeformAnalysisFormContext';
import { DRAG_AND_DROP_STATE_ACTION } from '../FreeformAnalysisForm/FreeformAnalysisFormUtil/dragAndDropReducer';
import DROPPABLE_LOCATION_IDS from './droppableLocationIds';
import PowerPivotLibraryItemCard from '../../common/LibraryItemCard/PowerPivot/components/PowerPivotLibraryItemCard.tsx';
import { isPowerPivotLibraryItem } from '../../common/LibraryItemCard/PowerPivot/constants/PowerPivotTypes.ts';

import './DraggableLibraryItemCard.scss';

const renderableContentTypes = [
  Constants.LIBRARY_ITEM_TYPES.EXCEL_CHART.value,
  Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value,
  Constants.LIBRARY_ITEM_TYPES.EXCEL_CELL_RANGE.value,
  Constants.LIBRARY_ITEM_TYPES.POWER_POINT_GROUP.value,
];

const powerBiTypes = [
  Constants.LIBRARY_ITEM_TYPES.POWER_BI_REPORT.value,
  Constants.LIBRARY_ITEM_TYPES.POWER_BI_REPORT_PAGE.value,
];

// disable all react-beautiful-dnd development warnings
window['__react-beautiful-dnd-disable-dev-warnings'] = true;

const DraggableLibraryItemCard = ({
  droppableLocationId,
  droppableLocations,
  /**
   * Shape is like a redux action
   *  {
   *    type: undefined | 'freeFormAnalysisEdit'
   *    payload: any
   *  }
   */
  forView,
  hideExcelCellRangeFormatSwitch,
  index,
  libraryItem,
  loading = false,
  panelNumber,
}) => {
  const dataItem = useMemo(() => {
    try {
      return JSON.parse(libraryItem.data);
    } catch {
      return libraryItem.data;
    }
  }, [libraryItem.data]);

  const { SvgScaleFactor: dataSvgScaleFactor } = dataItem || {};

  const [
    {
      draggableBetweenSectionsItemId,
      currentDroppableSectionId,
      currentPosition,
      currentSections,
    },
    dispatch,
  ] = useSections();
  const reduxDispatch = useDispatch();
  const { projectId } = useParams();
  // const { setDraggedItem } = useDragAndDrop();
  const memoizedLibraryItem = React.useRef(libraryItem);
  const [isDragDisabled, setIsDragDisabled] = useState(false);
  const [isLibraryItemComponentLoading, setIsLibraryItemComponentLoading] =
    useState(false);
  const [wrapperElement, setWrapperElement] = useState(null);
  const [valueSVGScale, setValueSVGScale] = useState(dataSvgScaleFactor * 100);
  const [SVGSliderProps, setSVGSliderProps] = useState(undefined);
  const debouncedOnChangeValueSVGScale = debounce(({ value }) => {
    setValueSVGScale(value);

    return postExcelLibraryItemSVGScaleFactor({
      projectId,
      dataRequest: value / 100,
      libraryItemId: libraryItem.id,
    });
  }, 500);

  const onChangeValueSVGScale = useCallback(
    ({ value }) => {
      debouncedOnChangeValueSVGScale({ value, projectId });
    },
    [debouncedOnChangeValueSVGScale, projectId]
  );

  const onComponentStateLibraryItem = useCallback(
    ({ type }) => {
      switch (type) {
        case 'LoadStart':
          setIsLibraryItemComponentLoading(true);
          break;

        case 'LoadEnd':
          setIsLibraryItemComponentLoading(false);
          break;

        default:
      }
    },
    [setIsLibraryItemComponentLoading]
  );
  const onClickFormatSwitcher = useCallback(
    (evt) => {
      evt.preventDefault();
      forView?.payload.onExcelCellRangeContentTypeChange({
        current: evt.target.getAttribute('data-name'),
        items: {
          excel: {
            cellRange: {
              id: libraryItem.id,
            },
          },
        },
      });
    },
    [forView?.payload, libraryItem.id]
  );

  const getValueRadio = (dataItm, libItem) => {
    const { type } = libItem;
    if (type === Constants.LIBRARY_ITEM_TYPES.POWER_POINT_GROUP.value) {
      return 'image';
    }
    if (
      type === Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value ||
      type === Constants.LIBRARY_ITEM_TYPES.EXCEL_CELL_RANGE.value
    ) {
      return dataItm?.FormatType === 'SVG' ? 'image' : 'web-table';
    }
    if (type === Constants.LIBRARY_ITEM_TYPES.EXCEL_CHART.value) {
      return 'image';
    }
    return 'web-table';
  };

  const componentTop = useMemo(() => {
    if (
      featureFlags.isSVGScalingEnabled &&
      forView?.type === 'freeFormAnalysisEdit' &&
      Constants.isSliderAvailableWhitelist.includes(libraryItem.type)
    ) {
      return (
        <LibraryItemCardWidgetOptions
          initialValueRadio={getValueRadio(dataItem, libraryItem)}
          items={{
            excel: {
              cellRange: {
                id: libraryItem.id,
              },
            },
          }}
          isDisabled={isLibraryItemComponentLoading || loading}
          propsSVGScaling={{
            propsSVGScalingControls: {
              onChangeValue: onChangeValueSVGScale,
              propsSlider: SVGSliderProps,
              valueSVGScale,
            },
          }}
          overrideValueRadio={getValueRadio(dataItem, libraryItem)}
          useLegacyFormatSwitcher
        />
      );
    }

    return null;
  }, [
    SVGSliderProps,
    dataItem,
    forView?.type,
    isLibraryItemComponentLoading,
    libraryItem,
    loading,
    onChangeValueSVGScale,
    valueSVGScale,
  ]);

  const isAtTheBottomOfCurrentColumn = () => {
    const { libraryItemIds } = droppableLocations[currentPosition.droppableId];

    const indexOfLastItem = libraryItemIds.includes(libraryItem.id)
      ? libraryItemIds.length - 1
      : libraryItemIds.length;

    return currentPosition.index === indexOfLastItem;
  };

  const canMoveUpToPrevSection = (initialIndex, dndPosition) => {
    if (dndPosition) {
      // when we in the first section and want to move a content by upArrow
      // we should check also if we are in the first box on the list...
      // this line do the trick :sad_frog
      const isFirstBoxInSection = dndPosition.droppableId.slice(-3) === '1-1';
      return !isFirstBoxInSection && dndPosition.index === 0;
    }
    return initialIndex === 0;
  };

  const canMoveDownToNextSection = (dndPosition) => {
    if (dndPosition && isAtTheBottomOfCurrentColumn()) {
      const isLastSection =
        Number(dndPosition.droppableId.slice(-1)) === currentSections.length;

      return !isLastSection;
    }
    return false;
  };

  const setDraggableBetweenSectionsItemId = useCallback(
    (libraryItemId) => {
      dispatch({
        type: DRAG_AND_DROP_STATE_ACTION.ITEM_ID_UPDATED,
        payload: { draggableBetweenSectionsItemId: libraryItemId },
      });
    },
    [dispatch]
  );
  const moveItemToAdjacentSection = (type) => {
    setDraggableBetweenSectionsItemId(libraryItem.id);
    wrapperElement.dispatchEvent(
      new KeyboardEvent('keydown', { keyCode: Constants.space, bubbles: false })
    );

    const currentSection = currentSections.find((s) =>
      s.items.map((i) => i.id).includes(libraryItem.id)
    );

    dispatch({
      type,
      payload: {
        libraryItemId: libraryItem.id,
        currentSectionOrder: currentSection ? currentSection.order : 1,
      },
    });
  };

  const canMoveToAdjacentArea = (position, keyCode) => {
    // check whether draggable can move to area above or below (within same section)
    const currentDroppableLocation = droppableLocations[position.droppableId];
    if (keyCode === Constants.arrowUp && currentDroppableLocation.areaAbove) {
      // return area above
      return currentDroppableLocation.areaAbove;
    }
    if (keyCode === Constants.arrowDown && currentDroppableLocation.areaBelow) {
      // return area below
      return currentDroppableLocation.areaBelow;
    }
    return false;
  };

  const updateCurrentPosition = (position, itemId) => {
    dispatch({
      type: DRAG_AND_DROP_STATE_ACTION.ITEM_MOVED_WITHIN_SECTION,
      payload: {
        currentPosition: position,
        draggableWithinSectionsItemId: itemId,
      },
    });
    wrapperElement.dispatchEvent(
      new KeyboardEvent('keydown', { keyCode: Constants.space, bubbles: false })
    );
  };

  const handleOnKeyDown = (keyCode) => {
    if (currentPosition) {
      // TODO: Revisit to improve
      // check if arrowUp or arrowDown
      if (keyCode === Constants.arrowUp || keyCode === Constants.arrowDown) {
        reduxDispatch(
          setLibraryItemStartPosition({
            start: currentPosition,
            draggableId: libraryItem.id,
          })
        );
        // check if droppableArea above or droppableArea below
        const adjacentArea = canMoveToAdjacentArea(currentPosition, keyCode);
        if (adjacentArea) {
          // execute code to update position with new currentPosition
          const newPosition = {
            droppableId: adjacentArea,
            index: 0,
          };
          reduxDispatch(setLibraryItemFinishPosition({ finish: newPosition }));
          updateCurrentPosition(newPosition, libraryItem.id);
        } else if (
          // if no adjacent droppableArea above or below, then check for adjacent section above or below
          keyCode === Constants.arrowUp &&
          canMoveUpToPrevSection(index, currentPosition)
        ) {
          moveItemToAdjacentSection(
            DRAG_AND_DROP_STATE_ACTION.ITEM_MOVED_UP_TO_NEW_SECTION
          );
        } else if (
          keyCode === Constants.arrowDown &&
          canMoveDownToNextSection(currentPosition)
        ) {
          moveItemToAdjacentSection(
            DRAG_AND_DROP_STATE_ACTION.ITEM_MOVED_DOWN_TO_NEW_SECTION
          );
        }
      }
    }
  };

  useEffect(() => {
    const getSectionOrder = () => {
      if (droppableLocationId === DROPPABLE_LOCATION_IDS.SOURCE_TRAY_ID)
        return 0;
      return Number(droppableLocationId.slice(-1));
    };

    if (
      wrapperElement &&
      draggableBetweenSectionsItemId === libraryItem.id &&
      currentDroppableSectionId === getSectionOrder()
    ) {
      wrapperElement.focus();
      wrapperElement.dispatchEvent(
        new KeyboardEvent('keydown', {
          keyCode: Constants.space,
          bubbles: false,
        })
      );
      setDraggableBetweenSectionsItemId(null);
    }
  }, [
    currentDroppableSectionId,
    draggableBetweenSectionsItemId,
    droppableLocationId,
    libraryItem.id,
    setDraggableBetweenSectionsItemId,
    wrapperElement,
  ]);

  const fieldRef = useRef();
  const figureElement = document?.querySelector(
    '.ck-widget_with-selection-handle'
  );
  if (figureElement) {
    figureElement.setAttribute('contenteditable', 'true');
  }
  return (
    <Draggable
      draggableId={libraryItem.id.toString()}
      index={index}
      isDragDisabled={isDragDisabled}
    >
      {(draggableProvided, draggableSnapshot) => {
        return (
          <>
            {componentTop}

            <div
              className="draggable-library-item-card2"
              data-testid="draggable-library-item-card"
              {...draggableProvided.draggableProps}
              {...draggableProvided.dragHandleProps}
              ref={(myRef) => {
                draggableProvided.innerRef(myRef);
                setWrapperElement(myRef);
              }}
              onKeyDown={(e) =>
                handleOnKeyDown(e.keyCode, draggableProvided.innerRef)
              }
              style={{ zIndex: 6 - panelNumber }}
              data-draggable-mode={!!currentPosition}
            >
              {libraryItem.type ===
                Constants.LIBRARY_ITEM_TYPES.RICH_TEXT.value && (
                <RichTextLibraryItemCard
                  libraryItem={libraryItem}
                  setIsDragDisabled={setIsDragDisabled}
                  isDragging={draggableSnapshot.isDragging}
                  innerRef={fieldRef}
                  libraryItemId={libraryItem.id}
                >
                  {libraryItem.data}
                </RichTextLibraryItemCard>
              )}

              {powerBiTypes.includes(libraryItem.type) && (
                <LibraryItemCard
                  innerRef={fieldRef}
                  libraryItemId={libraryItem.id}
                >
                  <PowerBILibraryItemCard
                    setIsDragDisabled={setIsDragDisabled}
                    isDragging={draggableSnapshot.isDragging}
                    libraryItem={memoizedLibraryItem.current}
                    projectId={projectId}
                  />
                </LibraryItemCard>
              )}

              {!libraryItem.error && isTableauLibraryItem(libraryItem.type) && (
                <LibraryItemCard
                  innerRef={fieldRef}
                  libraryItemId={libraryItem.id}
                >
                  <TableauPreview
                    report={{
                      ...libraryItem,
                      reportId:
                        libraryItem.reportId ||
                        _get(libraryItem, ['data', 'ReportId'], undefined) ||
                        _get(JSON.parse(libraryItem.data), 'ReportId', ''),
                    }}
                    hideTabs={!libraryItem.allTabsSelected}
                  />
                </LibraryItemCard>
              )}

              {featureFlags.isSVGScalingEnabled &&
              forView?.type === 'freeFormAnalysisEdit' &&
              Constants.isSliderAvailableWhitelist.includes(
                libraryItem.type
              ) ? (
                <LibraryItemCard
                  innerRef={fieldRef}
                  libraryItemId={libraryItem.id}
                >
                  <ExcelPPTLibraryItemCardNew
                    forView={forView}
                    formatSwitcherOnClick={onClickFormatSwitcher}
                    libraryItem={libraryItem}
                    projectID={projectId}
                    onInitializeSliderProps={setSVGSliderProps}
                    valueSVGScalePercent={valueSVGScale || 100}
                    onComponentState={onComponentStateLibraryItem}
                  />
                </LibraryItemCard>
              ) : (
                renderableContentTypes.includes(libraryItem.type) && (
                  <LibraryItemCard
                    innerRef={fieldRef}
                    libraryItemId={libraryItem.id}
                  >
                    <ExcelPPTLibraryItemCard
                      forView={forView}
                      hideExcelCellRangeFormatSwitch={
                        hideExcelCellRangeFormatSwitch
                      }
                      libraryItem={libraryItem}
                      projectId={projectId}
                      setIsDragDisabled={setIsDragDisabled}
                    />
                  </LibraryItemCard>
                )
              )}

              {isPowerPivotLibraryItem(libraryItem.type) && (
                <LibraryItemCard
                  innerRef={fieldRef}
                  libraryItemId={libraryItem.id}
                >
                  <PowerPivotLibraryItemCard
                    libraryItem={libraryItem}
                    projectId={projectId}
                    setIsDragDisabled={setIsDragDisabled}
                  />
                </LibraryItemCard>
              )}
            </div>
          </>
        );
      }}
    </Draggable>
  );
};

export default React.memo(DraggableLibraryItemCard);
